import { motion } from "framer-motion";
import { Mail, Shield, AlertTriangle } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";

const ContactPage = () => {
  return (
    <DashboardLayout currentPage="contact">
      <div className="p-4 sm:p-6 max-w-3xl mx-auto w-full overflow-x-hidden">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-2">Contact AI Manifest</h1>
          <p className="text-aura-text-secondary">We're here to help with any questions</p>
        </motion.div>

        {/* Contact Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card-aura p-4 sm:p-8 mb-6 sm:mb-8"
        >
          <div className="flex flex-col sm:flex-row items-start gap-4 mb-6">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-aura-emerald/10 flex items-center justify-center flex-shrink-0">
              <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-aura-emerald" />
            </div>
            <div className="w-full">
              <h2 className="font-heading text-lg sm:text-xl font-semibold text-white mb-2">Get in Touch</h2>
              <p className="text-aura-text-secondary text-sm sm:text-base mb-4">
                For any account, subscription, or technical support enquiries, please contact us at:
              </p>
              <a 
                href="mailto:support@aimanifest.com.au"
                className="inline-flex items-center gap-2 text-aura-emerald hover:text-aura-emerald-glow transition-colors text-base sm:text-lg font-medium break-all"
                data-testid="contact-email-link"
              >
                <Mail className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                support@aimanifest.com.au
              </a>
            </div>
          </div>
        </motion.div>

        {/* About Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card-aura p-4 sm:p-8 mb-6 sm:mb-8"
        >
          <div className="flex flex-col sm:flex-row items-start gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-white/5 flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-aura-text-secondary" />
            </div>
            <div>
              <h2 className="font-heading text-lg sm:text-xl font-semibold text-white mb-3">About AI Manifest</h2>
              <p className="text-aura-text-secondary text-sm sm:text-base leading-relaxed">
                AURA analyses historical lottery draw data using data-driven number analysis and statistical methods to generate well-balanced number combinations. The system evaluates historical draw patterns and probability distributions to suggest balanced lottery entries.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Responsible Gambling Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card-aura p-4 sm:p-8 border-aura-gold/20 bg-gradient-to-br from-aura-gold/5 to-transparent"
        >
          <div className="flex flex-col sm:flex-row items-start gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-aura-gold/10 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-aura-gold" />
            </div>
            <div>
              <h2 className="font-heading text-lg sm:text-xl font-semibold text-aura-gold mb-3">Responsible Gambling</h2>
              <p className="text-aura-text-secondary text-sm sm:text-base leading-relaxed">
                AI Manifest (AURA) is a data-driven lottery tracking and number analysis system. It does not guarantee lottery wins. Lottery results are determined by random draws. Always play responsibly and only spend what you can afford. For help, visit gamblinghelponline.org.au or call 1800 858 858.
              </p>
              <div className="mt-4 pt-4 border-t border-white/10">
                <p className="text-xs sm:text-sm text-aura-text-muted">
                  If you need help with gambling, please visit{" "}
                  <a 
                    href="https://www.gamblinghelponline.org.au" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-aura-emerald hover:underline"
                  >
                    Gambling Help Online
                  </a>
                  {" "}or call{" "}
                  <a href="tel:1800858858" className="text-aura-emerald hover:underline">
                    1800 858 858
                  </a>
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
};

export default ContactPage;
